<template>
    <v-dialog :value="value" @input="$emit('input', $event)" fullscreen hide-overlay transition="dialog-bottom-transition">
        <v-card>
            <v-toolbar dark color="primary" dense>
                <v-btn icon dark @click="$emit('input', false)">
                    <v-icon>{{ icons.mdiClose }}</v-icon>
                </v-btn>
                <v-toolbar-title>Comparison View</v-toolbar-title>
                <v-spacer></v-spacer>
                    <v-btn dark text @click="$emit('input', false)">Close</v-btn>
            </v-toolbar>

            <v-card-text class="pa-4">
                <v-row align="center" class="mb-4">
                    <!-- Metric Selector -->
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="selectedCompMetric"
                            :items="compMetricOptions"
                            label="Metric"
                            outlined dense hide-details
                        ></v-select>
                    </v-col>

                    <!-- Reference Image Selector -->
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="referenceImageId"
                            :items="imagesToCompare"
                            item-text="name"
                            item-value="id"
                            label="Reference (Control)"
                            outlined dense hide-details
                            prepend-inner-icon="mdi-flag-variant"
                        ></v-select>
                    </v-col>

                    <!-- Confidence Level Selector -->
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="confidenceLevel"
                            :items="confidenceOptions"
                            label="Stat Testing Level"
                            outlined dense hide-details
                            :disabled="['duration', 'ttff'].includes(selectedCompMetric)"
                        ></v-select>
                    </v-col>
                    
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center text-caption">
                            <v-icon small color="success" class="mr-1">{{ icons.mdiArrowUpBold }}</v-icon> Sig. Higher
                            <v-icon small color="error" class="ml-2 mr-1">{{ icons.mdiArrowDownBold }}</v-icon> Sig. Lower
                        </div>
                    </v-col>
                </v-row>

                <!-- UPDATED TABLE -->
                <v-data-table
                    :headers="comparisonHeaders"
                    :items="visibleComparisonItems"
                    class="elevation-1 comparison-table"
                    disable-pagination
                    hide-default-footer
                    fixed-header
                    height="70vh"
                >
                    <template v-slot:item="{ item, headers }">
                        <tr :class="{'grey lighten-5': item.is_net}">
                            <td v-for="header in headers" :key="header.value" 
                                :class="{ 
                                    'text-center': header.value !== 'aoi_name',
                                    'blue lighten-5': item[header.value] && item[header.value].isRef
                                }"
                            >
                                <!-- 1. AOI Name Column (With Hierarchy) -->
                                <template v-if="header.value === 'aoi_name'">
                                    <div :class="{ 
                                        'pl-6': item.is_child, 
                                        'font-weight-black': item.is_net,
                                    }" class="d-flex align-center">
                                        
                                        <!-- Net Icon -->
                                        <v-icon v-if="item.is_net" small left color="primary">{{ icons.mdiGroup }}</v-icon>
                                        
                                        <!-- Child Arrow -->
                                        <v-icon v-if="item.is_child" small left color="grey">{{ icons.mdiSubdirectoryArrowRight }}</v-icon>
                                        
                                        {{ item.aoi_name }}
                                    </div>
                                </template>

                                <!-- 2. Data Columns (Safe Check Added) -->
                                <template v-else>
                                    <div v-if="item[header.value]" class="d-flex align-center justify-center">
                                        <span :class="{ 'font-weight-bold': item.is_net || item[header.value].isRef }">
                                            {{ item[header.value].display }}
                                        </span>
                                        
                                        <v-icon v-if="item[header.value].sig === 'higher'" small color="success" class="ml-1">{{ icons.mdiArrowUpBold }}</v-icon>
                                        <v-icon v-if="item[header.value].sig === 'lower'" small color="error" class="ml-1">{{ icons.mdiArrowDownBold }}</v-icon>
                                    </div>
                                    <div v-else class="text-center">-</div>
                                </template>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { 
    mdiArrowUpBold,
    mdiArrowDownBold,
    mdiClose,
    mdiGroup,
    mdiSubdirectoryArrowRight
} from '@mdi/js'

import { ref, computed, watch } from '@vue/composition-api'

export default {
    props: ['value', 'itemsToCompare', 'comparisonHeaders', 'imagesToCompare'],
    setup(props, {emit}) {
        const selectedCompMetric = ref('noting');
        const compMetricOptions = ref([
            {text:'Noting', value:'noting'}, 
            {text:'Visit Duration', value:'duration'},
            {text:'Order', value:'order'},
            {text:'Time to 1st fixation', value:'ttff'}, 
            {text:'Element noted 1st', value:'noted_first'}, 
            {text:'Element coverage', value:'coverage'}
        ]);

        // --- STAT TESTING STATE ---
        const referenceImageId = ref(null);
        const confidenceLevel = ref(95);
        const confidenceOptions = [
            { text: '80% Confidence', value: 80, zScore: 1.28 },
            { text: '85% Confidence', value: 85, zScore: 1.44 },
            { text: '90% Confidence', value: 90, zScore: 1.645 },
            { text: '95% Confidence', value: 95, zScore: 1.96 },
            { text: '99% Confidence', value: 99, zScore: 2.576 }
        ];

        const visibleComparisonItems = computed(() => {
            const isPercentageMetric = ['noting', 'noted_first', 'coverage'].includes(selectedCompMetric.value);

            return (props.itemsToCompare || []).map(row => {
                const aoi_name = String(row.aoi_name).trim().toLowerCase()
                const newRow = { 
                    aoi_name: aoi_name,
                    is_net: row.is_net,
                    is_child: row.is_child
                };
                
                const refKey = `img_${referenceImageId.value}_raw`;
                const refObj = row[refKey]; 
                let refValue = null;
                let refN = 0;

                if (refObj && refObj.val && refObj.val[selectedCompMetric.value] !== undefined) {
                    refValue = refObj.val[selectedCompMetric.value];
                    refN = refObj.n;
                }

                Object.keys(row).forEach(key => {
                    if (key.endsWith('_raw')) {
                        const colId = key.replace('_raw', '');
                        const rawObj = row[key];
                        
                        const cellData = {
                            display: '-',
                            sig: 'none',
                            isRef: colId === `img_${referenceImageId.value}`
                        };

                        if (rawObj && rawObj.val && rawObj.val[selectedCompMetric.value] !== undefined) {
                            const val = rawObj.val[selectedCompMetric.value];
                            const n = rawObj.n;

                            if (selectedCompMetric.value.includes('duration') || selectedCompMetric.value.includes('ttff')) {
                                cellData.display = Math.round(val) + ' ms';
                            } else {
                                cellData.display = val.toFixed(1) + '%';
                                if (isPercentageMetric && !cellData.isRef && refValue !== null) {
                                    cellData.sig = calculateSignificance(refValue, refN, val, n);
                                }
                            }
                        }
                        
                        if (cellData.isRef) cellData.sig = 'ref';

                        newRow[colId] = cellData;
                    }
                });
                console.log(newRow)
                return newRow;
            });
        });

        const calculateSignificance = (p1, n1, p2, n2) => {
            if (!n1 || !n2) return 'none';

            const prop1 = p1 / 100;
            const prop2 = p2 / 100;

            const pPool = ((prop1 * n1) + (prop2 * n2)) / (n1 + n2);
            const se = Math.sqrt(pPool * (1 - pPool) * ((1/n1) + (1/n2)));
            
            if (se === 0) return 'none';

            const z = (prop2 - prop1) / se;
            const threshold = confidenceOptions.find(o => o.value === confidenceLevel.value).zScore;

            if (z > threshold) return 'higher';
            if (z < -threshold) return 'lower';
            return 'none';
        };

        watch(
            () => props.value,
            (newVal) => {
                if (newVal && props.imagesToCompare && props.imagesToCompare.length > 1) {
                    // Update reference image to the first selected item, unless it already equals one of the selected ones
                    if (!referenceImageId.value || !props.imagesToCompare.some(img => img.id === referenceImageId.value)) {
                        referenceImageId.value = props.imagesToCompare[0].id;
                    }
                }
            }
        )

        return {
            selectedCompMetric,
            compMetricOptions,
            referenceImageId,
            confidenceLevel,
            confidenceOptions,
            visibleComparisonItems,
            icons: {
                mdiArrowUpBold,
                mdiArrowDownBold,
                mdiClose,
                mdiGroup,
                mdiSubdirectoryArrowRight
            }
        }
    }
}
</script>